import {
  Banknote,
  ChartNoAxesCombined,
  BookOpenText,
  BriefcaseBusiness,
  Building2,
  CheckSquare2,
  Clock3,
  ContactRound,
  CreditCard,
  FileText,
  Gauge,
  Layers3,
  Settings,
  ShieldCheck
} from 'lucide-react';
import type { ModuleSummary } from '@ddone/shared-types';
import type { ComponentType } from 'react';

export interface NavigationItem {
  id: string;
  label: string;
  href: string;
  moduleId?: string;
  permission?: string;
  anyPermissions?: string[];
  icon: ComponentType<{ size?: number; 'aria-hidden'?: boolean | 'true' }>;
  section?: 'workspace' | 'system';
  superAdminOnly?: boolean;
}

export const navigation: NavigationItem[] = [
  { id: 'dashboard', label: 'Panoramica', href: '/', icon: Gauge, section: 'workspace' },
  {
    id: 'clients',
    label: 'Clienti',
    href: '/clients',
    moduleId: 'clients',
    permission: 'clients.client.read',
    icon: ContactRound,
    section: 'workspace'
  },
  {
    id: 'crm',
    label: 'CRM',
    href: '/crm',
    moduleId: 'crm',
    permission: 'crm.opportunity.read',
    icon: BriefcaseBusiness,
    section: 'workspace'
  },
  {
    id: 'projects',
    label: 'Progetti',
    href: '/projects',
    moduleId: 'projects',
    permission: 'projects.project.read',
    icon: Building2,
    section: 'workspace'
  },
  {
    id: 'notes',
    label: 'Note',
    href: '/notes',
    moduleId: 'notes',
    permission: 'notes.document.read',
    icon: FileText,
    section: 'workspace'
  },
  {
    id: 'tasks',
    label: 'Attività',
    href: '/tasks',
    moduleId: 'tasks',
    permission: 'tasks.task.read',
    icon: CheckSquare2,
    section: 'workspace'
  },
  {
    id: 'time',
    label: 'Tempo',
    href: '/time',
    moduleId: 'time-tracking',
    permission: 'time.entry.read',
    icon: Clock3,
    section: 'workspace'
  },
  {
    id: 'billing',
    label: 'Scadenze',
    href: '/billing',
    moduleId: 'billing',
    permission: 'billing.item.read',
    icon: CreditCard,
    section: 'workspace'
  },
  {
    id: 'expenses',
    label: 'Spese',
    href: '/expenses',
    moduleId: 'expenses',
    permission: 'expenses.expense.read',
    icon: Banknote,
    section: 'workspace'
  },
  {
    id: 'analytics',
    label: 'Analytics',
    href: '/analytics',
    anyPermissions: ['billing.item.read', 'expenses.expense.read'],
    icon: ChartNoAxesCombined,
    section: 'workspace'
  },
  {
    id: 'help',
    label: 'Guida',
    href: '/help',
    icon: BookOpenText,
    section: 'system'
  },
  {
    id: 'settings',
    label: 'Impostazioni',
    href: '/settings',
    permission: 'settings.workspace.read',
    icon: Settings,
    section: 'system'
  },
  {
    id: 'access',
    label: 'Accessi',
    href: '/admin/access',
    permission: 'users.permission.manage',
    icon: ShieldCheck,
    section: 'system'
  },
  {
    id: 'workspaces',
    label: 'Workspace ERP',
    href: '/admin/workspaces',
    icon: Layers3,
    section: 'system',
    superAdminOnly: true
  }
];

export function navigationForAccess(
  items: readonly NavigationItem[],
  modules: readonly ModuleSummary[],
  hasPermission: (permission?: string) => boolean,
  superAdmin = false
): NavigationItem[] {
  const modulesById = new Map(modules.map((module) => [module.id, module]));
  return items.filter((item) => {
    if (item.superAdminOnly && !superAdmin) return false;
    if (!hasPermission(item.permission)) return false;
    if (item.anyPermissions && !item.anyPermissions.some(hasPermission)) return false;
    if (!item.moduleId) return true;
    const module = modulesById.get(item.moduleId);
    return Boolean(
      module?.enabled &&
      (module.requiredPermissions.length === 0 || module.requiredPermissions.some(hasPermission))
    );
  });
}
